<template>
  <div class="modal-container">
    <div class="modal-content" v-if="!loading">
      <md-list class="md-double-line">
        <div class="header-section">
          <div class="has-text-weight-semibold is-size-6 mb-3">
            {{ selectedType === 'distance' ? '' : 'Update' }} {{ getTypeDisplayName(selectedType) }} Zone
          </div>
          <div class="type-selector">
            <span class="tag is-small">
              {{ getTypeDisplayName(selectedType) }}
            </span>
          </div>
        </div>
        <div class="is-size-6 has-text-weight-semibold has-text-danger">
          {{ msg }}
        </div>

        <!-- Geofence Section -->
        <div v-if="selectedType === 'geofence'">
          <!-- Distribution Hub Selector -->
          <div class="field has-addons mb-1 mt-3">
            <div class="control is-expanded">
              <GlobalSelector v-model="selectedHub" label="Distribution Zone" type="single" dataSource="hubs"
                placeholder="Select a distribution zone..." :disabled="!editable" />
            </div>
          </div>

          <!-- Days of Service Section -->
          <div class="field mt-3 days-of-service">
            <label class="label is-size-7 has-text-weight-semibold mb-2">Days of Service</label>
            <div class="days-container">
              <label class="checkbox" v-for="(day, key) in daysOfService" :key="key">
                <input type="checkbox" v-model="daysOfService[key]" :disabled="!editable" class="mr-1" />
                {{ getDayLabel(key) }}
              </label>
            </div>
          </div>

          <!-- Color Picker Section -->
          <div class="field mt-3" v-if="editable">
            <label class="label is-size-7 has-text-weight-semibold mb-2">Shape Color</label>
            <div class="color-input-container">
              <div class="color-picker-wrapper">
                <input type="color" v-model="selectedColor" class="color-picker" />
              </div>
            </div>
          </div>
        </div>

        <!-- Billable Section -->
        <div v-if="selectedType === 'billable'">
          <div class="field has-addons mb-1">
            <div class="control is-expanded">
              <GlobalSelector v-model="zone" label="Zone" type="single" dataSource="zones"
                placeholder="Select a zone..." :disabled="!editable" zoneType="billable" />
            </div>
          </div>

          <div class="is-flex is-flex-direction-column is-flex-wrap-wrap is-align-items-baseline mt-1 mb-1"
            v-if="zone.value">
            <GlobalSelector v-model="selectedClients" label="Clients" type="multi" dataSource="clients"
              placeholder="Select clients..." :disabled="true" />
          </div>
        </div>

        <!-- Payable Section -->
        <div v-if="selectedType === 'payable'">
          <div class="field has-addons mb-1">
            <div class="control is-expanded">
              <GlobalSelector v-model="zone" label="Zone" type="single" dataSource="zones"
                placeholder="Select a zone..." :disabled="!editable" zoneType="payable" />
            </div>
          </div>

          <div class="is-flex is-flex-direction-column is-flex-wrap-wrap is-align-items-baseline mt-1 mb-1"
            v-if="zone.value">
            <GlobalSelector v-model="selectedContractors" label="Contractors" type="multi" dataSource="contractors"
              placeholder="Select contractors..." :disabled="true" />
          </div>
        </div>

        <!-- Distance Section -->
        <div v-if="selectedType === 'distance'">
          <!-- Center Location Display (Read-only) -->
          <div class="field mb-1 mt-3">
            <label class="label is-size-7 has-text-weight-semibold mb-1">{{ shape.value }}</label>
          </div>
          <div class="field mb-1 mt-1">
            <hr />
          </div>
          <!-- Center Location Selector -->
          <div class="field has-addons mb-3 mt-2">
            <div class="control is-expanded">
              <GlobalSelector v-model="centerLocation" label="Center Location" type="single" dataSource="hubs"
                placeholder="Select center location..." :disabled="true" />
            </div>
          </div>

          <!-- Min/Max Kilometer Fields -->
          <div class="columns is-multiline mb-2">
            <div class="column is-half">
              <div class="field">
                <label class="label is-size-7 has-text-weight-semibold mb-1">Min Kilometer:</label>
                <div class="control">
                  <input class="input is-small" type="number" v-model="minKm" :disabled="true" />
                </div>
              </div>
            </div>
            <div class="column is-half">
              <div class="field">
                <label class="label is-size-7 has-text-weight-semibold mb-1">Max Kilometer:</label>
                <div class="control">
                  <input class="input is-small" type="number" v-model="maxKm" :disabled="true" />
                </div>
              </div>
            </div>
          </div>

          <!-- Color Picker Section -->
          <div class="field mt-3" v-if="editable">
            <label class="label is-size-7 has-text-weight-semibold mb-2">Shape Color</label>
            <div class="color-input-container">
              <div class="color-picker-wrapper">
                <input type="color" v-model="selectedColor" class="color-picker"
                  :disabled="selectedType === 'distance'" />
              </div>
            </div>
          </div>
        </div>

        <button v-if="selectedType !== 'distance'"
        class="button is-small is-success is-fullwidth mt-5" @click="editFirestore"
          >
          Save
        </button>

        <!-- Action Buttons -->
        <div v-if="editable && selectedType !== 'distance'" class="action-buttons mt-3">
          <div class="field has-addons">
            <div class="control">
              <button class="button is-small" @click="addMoreShape()">
                Add By Drawing
              </button>
            </div>
            <div class="control">
              <button class="button is-small" @click="deletePartsShape()">
                Delete By Drawing
              </button>
            </div>
            <div class="control">
              <button :class="isFSAButtonLoading
                ? 'button is-small is-loading'
                : 'button is-small'
                " :disabled="!editable" @click="editSelectableFSAMode()">
                Edit In FSA Mode
              </button>
            </div>
          </div>
        </div>
      </md-list>
    </div>
    <div class="loader-container" v-else>
      <PulseLoader />
    </div>
  </div>
</template>

<script>
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import store from "../store";
import { zonesInfoCollection } from "../store/fireStore";
import { changeZoneId, updatePostalCode, findOne, updateZoneWithSync } from "../store/mongoStore";
import EventHandler from "../EventHandler";
import { unitConverter } from "@/utils/utils";
import GlobalSelector from "./GlobalSelector.vue";
import { circle as circleTurf } from "@turf/turf";

export default {
  components: {
    PulseLoader,
    GlobalSelector,
  },
  data() {
    return {
      environment: this.store.state.environment,
      zone: {
        value: "",
        zoneNameId: "",
        shapesId: "",
      },
      msg: "",
      loading: false,
      isFSAButtonLoading: false,
      selectedHub: null,
      selectedClients: [], // Array of selected client IDs
      selectedContractors: [], // Array of selected contractor IDs
      selectedType: 'geofence', // Default to geofence
      selectedColor: '#ff0000', // Default color for geofence shapes
      minKm: 0, // Min kilometer for distance shapes
      maxKm: 0, // Max kilometer for distance shapes
      centerLocation: null, // Center location for distance shapes
      daysOfService: {
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false,
      },
    };
  },
  props: {
    coordinates: {
      type: Array,
    },
    shape: {
      type: Object,
    },
    vehicles: {
      type: Array,
    },
    store: {
      type: Object,
    },
    editable: {
      type: Boolean,
    },
    distributionZones: {
      type: Array,
      default: () => []
    },
  },
  watch: {
    /**
     * Watch for zone changes and update selected clients/contractors
     */
    'zone.zoneNameId': {
      handler(newZoneId, oldZoneId) {
        if (newZoneId && newZoneId !== oldZoneId) {
          // Get zone data and update selected entities based on zone type
          const zone = this.store.state.zoneInformations?.find(z => z.id === newZoneId);
          if (zone) {
            if (zone.type === 'payable') {
              // For contractors, just pass the IDs directly - GlobalSelector will handle the display
              this.selectedContractors = zone.clients || [];
              this.selectedClients = [];
            } else {
              this.selectedClients = zone.clients || [];
              this.selectedContractors = [];
            }
          }
        } else if (!newZoneId) {
          // Clear entities when zone is cleared
          this.selectedClients = [];
          this.selectedContractors = [];
        }
      },
      immediate: false
    },
    /**
     * Watch for selected type changes and emit type change event
     */
    selectedType: {
      handler(newType, oldType) {
        if (newType !== oldType) {
          this.$emit('type-change', {
            type: newType,
            color: this.selectedColor
          });
        }
      },
      immediate: false
    },
    /**
     * Watch for color changes and emit type change event
     */
    selectedColor: {
      handler(newColor, oldColor) {
        if (newColor !== oldColor) {
          this.$emit('type-change', {
            type: this.selectedType,
            color: newColor
          });
        }
      },
      immediate: false
    }
  },
  methods: {
    closeModal() {
      EventHandler.$emit("closeModal");
    },
    addMoreShape() {
      this.addMoreShapeFunc(true);
    },
    deletePartsShape() {
      this.addMoreShapeFunc(false);
    },
    convertDistance(distance, isSaving = false) {
      const targetUnit = isSaving
        ? store.getters.DistanceUnit.dataSourceUnit
        : store.getters.DistanceUnit.selected;
      const sourceUnit = isSaving
        ? store.getters.DistanceUnit.selected
        : store.getters.DistanceUnit.dataSourceUnit;
      return unitConverter(sourceUnit, targetUnit, distance);
    },
    editSelectableFSAMode() {
      this.isFSAButtonLoading = true;
      setTimeout(async () => {
        await this.selectedFSAModeEdit();
        this.isFSAButtonLoading = false;
      }, 500);
    },
    async editFirestore() {
      this.loading = true;
      const zone = this.zone.value ? this.zone.value.trim() : "";
      const zoneId = this.zone.zoneNameId ? this.zone.zoneNameId.trim() : "";
      const shape = this.shape;
      const shapeId = shape.id.trim();
      const selectedDistributionZoneId = this.selectedHub?.DistributionZoneId || null;

      // Validate based on shape type
      if (this.selectedType === 'distance') {
        if (!this.centerLocation) {
          this.loading = false;
          this.msg = "Center location is required for distance shapes";
          return;
        }
        if (!this.minKm || !this.maxKm) {
          this.loading = false;
          this.msg = "Please enter both minimum and maximum kilometers";
          return;
        }
        if (Number(this.minKm) >= Number(this.maxKm)) {
          this.loading = false;
          this.msg = "Minimum kilometers must be less than maximum kilometers";
          return;
        }
      } else {
        // Validate at least one of zone or distribution zone is selected for other types
        if ((!zone || !zoneId) && !selectedDistributionZoneId) {
          this.loading = false;
          this.msg = "Please select at least one zone or distribution zone";
          return;
        }
      }

      try {
        // Check if zone has changed and update if needed
        const zoneChanged = zoneId !== (this.shape.zoneId || "").trim();
        const distributionZoneChanged =
          selectedDistributionZoneId !== (this.shape.distributionZoneId || null);
        const colorChanged = (this.selectedType === 'geofence' || this.selectedType === 'distance') &&
          this.selectedColor !== (this.shape.color || '#ff0000');
        const daysOfServiceChanged = JSON.stringify(this.getDaysOfServiceForAPI()) !==
          JSON.stringify(this.shape.daysOfService || {});
        const distanceChanged = this.selectedType === 'distance' && (
          Number(this.minKm) !== (this.shape.min || 0) ||
          Number(this.maxKm) !== (this.shape.max || 0)
        );
        const centerLocationChanged = this.selectedType === 'distance' &&
          this.centerLocation?.DistributionZoneId !== (this.shape.center?.id || null);

        if (zoneChanged || distributionZoneChanged || colorChanged || daysOfServiceChanged || distanceChanged || centerLocationChanged) {
          // Handle distance shape updates separately
          if (this.selectedType === 'distance') {
            await this.updateDistanceShape();
          } else {
            // Handle other shape types
            const daysOfServiceData = this.getDaysOfServiceForAPI(); // Get days of service data
            const { coordinates, newShapesId, multi, distributionZoneId } = await changeZoneId(
              zoneId, shapeId, selectedDistributionZoneId, false, this.selectedType, daysOfServiceData, this.selectedColor
            );

            const originalZone = this.store.state.zoneInformations?.find(z => z.id === this.shape.zoneId);

            // Determine which entities to pass based on type
            const originalZoneClientsIds = originalZone.clients || [];
            let newZoneClientsIds = [];
            if (this.selectedType === 'billable') {
              newZoneClientsIds = this.selectedClients;
            } else if (this.selectedType === 'payable') {
              // selectedContractors are now simple IDs, no need to extract
              newZoneClientsIds = this.selectedContractors;
            }

            await updatePostalCode(coordinates, newShapesId, multi, distributionZoneId, newZoneClientsIds, originalZoneClientsIds, daysOfServiceData, this.selectedType, this.selectedColor);
          }
        }

        // Update the shape properties
        this.shape.zone = zone;
        this.shape.zoneId = zoneId;
        this.shape.distributionZoneId = selectedDistributionZoneId;
        this.shape.daysOfService = this.getDaysOfServiceForAPI(); // Save days of service to shape
        this.shape.color = (this.selectedType === 'geofence' || this.selectedType === 'distance') ? this.selectedColor : null; // Save color for geofence and distance shapes

        // Update distance-specific properties
        if (this.selectedType === 'distance') {
          this.shape.min = Number(this.minKm);
          this.shape.max = Number(this.maxKm);
          this.shape.center = {
            id: this.centerLocation.DistributionZoneId,
            coordinates: {
              _lat: this.centerLocation.Center._lat,
              _long: this.centerLocation.Center._long,
            },
          };
          this.shape.distributionZoneId = this.centerLocation.DistributionZoneId;
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.msg = "Error in editing zone";
      }
    },
    /**
     * Determine the initial type based on existing shape data
     */
    determineInitialType() {
      // Check if it's a distance shape first
      if (this.shape.type === 'distance') {
        this.selectedType = 'distance';
        return;
      }

      // If shape has a zoneId, check if it's billable or payable
      if (this.shape.zoneId) {
        const zone = this.store.state.zoneInformations?.find(z => z.id === this.shape.zoneId);
        if (zone) {
          if (zone.type === 'payable') {
            this.selectedType = 'payable';
          } else if (zone.type === 'billable') {
            this.selectedType = 'billable';
          } else {
            // Legacy postal code zones default to billable
            this.selectedType = 'billable';
          }
        } else {
          // If zone not found, default to billable
          this.selectedType = 'billable';
        }
      } else if (this.shape.distributionZoneId) {
        // If only distribution zone is set, it's a geofence
        this.selectedType = 'geofence';
      } else {
        // Default to geofence
        this.selectedType = 'geofence';
      }
    },
    clearZone() {
      this.zone = { value: "", zoneNameId: "", shapesId: "" };
      this.selectedClients = []; // Clear selected clients when zone is cleared
    },
    clearHub() {
      this.selectedHub = null;
    },
    getDayLabel(key) {
      switch (key) {
        case 'mon': return 'Monday';
        case 'tue': return 'Tuesday';
        case 'wed': return 'Wednesday';
        case 'thu': return 'Thursday';
        case 'fri': return 'Friday';
        case 'sat': return 'Saturday';
        case 'sun': return 'Sunday';
        default: return key;
      }
    },
    /**
     * Convert daysOfService data to the format expected by the API
     */
    getDaysOfServiceForAPI() {
      return {
        mon: this.daysOfService.mon || false,
        tue: this.daysOfService.tue || false,
        wed: this.daysOfService.wed || false,
        thu: this.daysOfService.thu || false,
        fri: this.daysOfService.fri || false,
        sat: this.daysOfService.sat || false,
        sun: this.daysOfService.sun || false,
      };
    },
    getTypeDisplayName(type) {
      switch (type) {
        case 'geofence': return 'Geofence';
        case 'distance': return 'Distance';
        case 'billable': return 'Billable';
        case 'payable': return 'Payable';
        default: return type;
      }
    },
    /**
     * Generate shape coordinates for distance-based zones
     * @param {Object} centerLocation - The selected center location hub
     * @param {Number} maxKm - Maximum radius in kilometers
     * @param {Number} minKm - Minimum radius in kilometers (optional)
     * @returns {Object} Object containing coordinates and multi flag
     */
    generateDistanceZoneShape(centerLocation, maxKm, minKm = 0) {
      if (!centerLocation?.Center?._lat || !centerLocation?.Center?._long) {
        throw new Error('Invalid center location coordinates');
      }

      const center = [centerLocation.Center._long, centerLocation.Center._lat];

      // Create the outer circle with max radius
      const outerCircle = circleTurf(center, maxKm, { steps: 64, units: 'kilometers' });

      // If minKm is 0 or not provided, return the outer circle
      if (!minKm || minKm <= 0) {
        return {
          coordinates: outerCircle.geometry.coordinates[0],
          multi: false
        };
      }

      // If minKm is provided, create a donut shape by subtracting the inner circle
      const innerCircle = circleTurf(center, minKm, { steps: 64, units: 'kilometers' });

      // Create donut shape manually
      const donut = {
        type: "Polygon",
        coordinates: [
          outerCircle.geometry.coordinates[0], // outer ring
          innerCircle.geometry.coordinates[0].slice().reverse(), // inner ring (hole)
        ],
      };

      return {
        coordinates: donut.coordinates,
        multi: true,
      };
    },
    /**
     * Update distance shape in MongoDB
     */
    async updateDistanceShape() {
      try {
        // Generate new shape coordinates
        const shapeData = this.generateDistanceZoneShape(
          this.centerLocation,
          Number(this.maxKm),
          Number(this.minKm)
        );

        // Find and update the existing shape in MongoDB
        const existingShape = await findOne("zones", {
          shapesID: this.shape.zoneId,
          distributionZoneId: this.centerLocation.DistributionZoneId
        });
        if (existingShape) {
          // Update the shape coordinates and properties
          await updateZoneWithSync(
            { _id: existingShape._id },
            {
              $set: {
                coordinates: JSON.stringify(shapeData.coordinates),
                multi: shapeData.multi,
                color: this.selectedColor,
                distributionZoneId: this.centerLocation.DistributionZoneId,
              }
            }
          );

          console.log('Distance shape updated successfully');

          // Get the current zone data to update the centers array
          const zoneDoc = await zonesInfoCollection.doc(this.shape.zoneId).get();
          const zoneData = zoneDoc.data();

          // Prepare the new center data
          const newCenter = {
            id: this.centerLocation.DistributionZoneId,
            coordinates: {
              _lat: this.centerLocation.Center._lat,
              _long: this.centerLocation.Center._long,
            }
          };

          // Update the centers array - replace the center with matching ID
          const centers = zoneData.center || [];
          const existingCenterIndex = centers.findIndex(c => c.id === this.shape.distributionZoneId);

          if (existingCenterIndex >= 0) {
            // Update existing center in the array that matches the current shape's distributionZoneId
            centers[existingCenterIndex] = newCenter;
          } else {
            // If no matching center found, add new one (fallback)
            centers.push(newCenter);
          }

          await zonesInfoCollection.doc(this.shape.zoneId).update({
            center: centers, // Keep backward compatibility
          });

          console.log('Updated centers array for zone:', this.shape.zoneId, 'with centers:', centers);
        } else {
          console.warn('No existing shape found for distance shape:', this.shape.id);
        }
      } catch (error) {
        console.error('Error updating distance shape:', error);
        throw new Error('Failed to update distance shape');
      }
    },
    /**
     * Remove a center from the zone's centers array
     * @param {string} zoneId - The zone ID
     * @param {string} centerId - The center ID to remove
     */
    async removeCenterFromZone(zoneId, centerId) {
      try {
        const zoneDoc = await zonesInfoCollection.doc(zoneId).get();
        const zoneData = zoneDoc.data();

        if (zoneData && zoneData.centers) {
          const centers = zoneData.centers.filter(c => c.id !== centerId);

          await zonesInfoCollection.doc(zoneId).update({
            centers: centers,
            center: centers.length > 0 ? centers[0] : null // Keep backward compatibility
          });

          console.log('Removed center from zone:', zoneId, 'center:', centerId);
        }
      } catch (error) {
        console.error('Error removing center from zone:', error);
      }
    },
  },
  computed: {
    nameChanged() {
      return (this.shape.zone || "").trim() !== (this.zone.value || "").trim();
    },
    hubChanged() {
      return (this.shape.distributionZoneId || null) !== (this.selectedHub?.DistributionZoneId || null);
    },
    /**
     * Get available vendors from the store
     */
    availableVendors() {
      return this.store?.state?.vendors || [];
    },
    /**
     * Get the center location name for display
     */
    centerLocationName() {
      if (!this.centerLocation) return 'No center location selected';
      return this.centerLocation.DistributionZoneName || 'Unknown location';
    },
  },
  async mounted() {
    // Determine the initial type based on existing data
    this.determineInitialType();

    // Load existing distribution hub if available
    if (this.shape.distributionZoneId) {
      this.selectedHub = this.distributionZones.find(hub => hub.DistributionZoneId === this.shape.distributionZoneId);
    }

    // Load existing zone data if available
    if (this.shape.zoneId) {
      this.zone.value = this.shape.zone;
      this.zone.zoneNameId = this.shape.zoneId;

      const zoneDoc = await zonesInfoCollection.doc(this.shape.zoneId).get();
      const zoneData = zoneDoc.data();

      if (zoneData) {
        if (zoneData.type === 'payable') {
          // For contractors, just pass the IDs directly - GlobalSelector will handle the display
          this.selectedContractors = zoneData.clients || [];
        } else {
          this.selectedClients = zoneData.clients || [];
        }
      }
    }

    // Load existing days of service if available
    if (this.shape.daysOfService) {
      this.daysOfService = {
        mon: this.shape.daysOfService.mon || false,
        tue: this.shape.daysOfService.tue || false,
        wed: this.shape.daysOfService.wed || false,
        thu: this.shape.daysOfService.thu || false,
        fri: this.shape.daysOfService.fri || false,
        sat: this.shape.daysOfService.sat || false,
        sun: this.shape.daysOfService.sun || false,
      };
    }

    // Load existing color if available (for geofence and distance shapes)
    if (this.shape.color) {
      this.selectedColor = this.shape.color;
    }

    // Load distance shape data if available
    if (this.selectedType === 'distance') {
      this.minKm = this.shape.min || 0;
      this.maxKm = this.shape.max || 0;

      // Load center location
      if (this.shape.distributionZoneId) {
        this.centerLocation = this.distributionZones.find(hub => hub.DistributionZoneId === this.shape.distributionZoneId);
      }
    }
  },
};
</script>

<style>
/* Target only our specific EditShapeModal popup with the class we added */
.edit-shape-modal-popup .leaflet-popup-content-wrapper {
  padding: 20px !important;
  border-radius: 4px !important;
  width: 450px !important;
}

.edit-shape-modal-popup .leaflet-popup-content {
  width: auto !important;
  max-width: 450px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.edit-shape-modal-popup .leaflet-popup-tip {
  width: 10px !important;
  height: 10px !important;
  margin: -5px auto 0 !important;
}

.edit-shape-modal-popup .leaflet-popup-content>* {
  margin: 0 !important;
  padding: 0 !important;
}

/* Adjust clear button height */
.button {
  height: 36px !important;
  padding: 0 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Ensure the clear button icon is centered */
.button i {
  margin: 0 !important;
}
</style>

<style lang="scss" scoped>
@import "~leaflet/dist/leaflet.css";

/* Modal container for reduced width */
.modal-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
}

.modal-content {
  width: 100%;
  max-width: 450px;
  min-height: 300px !important;
  padding: 8px !important;
  overflow: visible !important;
  position: relative !important;
}

.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0;
  margin: 0;
}

/* No longer need global overrides, as we're targeting the specific class */

.custom .leaflet-popup-tip,
.custom .leaflet-popup-content-wrapper {
  background: #e93434;
  color: #ffffff;
}

.is-disabled {
  opacity: 0.65;
  cursor: not-allowed;

  :deep(.multiselect__tags) {
    background: #f5f5f5;
  }
}

.help {
  font-style: italic;
}

/* Color picker styling */
.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
}

.color-input-container {
  align-items: center;
}

.color-picker-wrapper {
  width: 36px;
  height: 30px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 8px;
}

.color-picker {
  width: 200%;
  height: 200%;
  transform: translate(-25%, -25%);
  cursor: pointer;
}

.tag {
  height: auto;
  white-space: normal;
  word-break: break-word;
}

/* Action buttons styling */
.action-buttons {
  .field.has-addons {
    width: 100%;

    .control {
      flex-grow: 1;
      flex-basis: 0;

      .button {
        width: 100%;
        border-radius: 0;
        justify-content: center;

        &:first-child {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        &:last-child {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
    }
  }
}

/* Additional compact styling */
.md-list {
  padding: 0 !important;
  margin: 0 !important;
}

.columns {
  margin: 0 !important;
}

.column {
  padding: 0.25rem !important;
}

.notification {
  padding: 0.3rem !important;
}

input.input.is-small {
  height: 28px !important;
  font-size: 0.75rem !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

/* Reduce the size of multiselect component */

.button.is-small {
  height: 28px !important;
  font-size: 0.75rem !important;
  padding-left: 0.5em !important;
  padding-right: 0.5em !important;
}

/* Ensure GlobalSelector doesn't overflow */
.field .control .global-selector-container {
  max-width: 100% !important;
  overflow: visible !important;
  position: relative !important;
}

.field .control .global-selector-container .selected-items-container {
  max-width: 100% !important;
  overflow: hidden !important;
}

.field .control .global-selector-container .selected-items-container .tags {
  max-width: 100% !important;
  overflow: hidden !important;
}

.field .control .global-selector-container .selected-items-container .tag {
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* Ensure dropdown can overflow modal */
.field .control .global-selector-container .multi-select-container {
  overflow: visible !important;
  position: relative !important;
  z-index: 9999 !important;
}

.field .control .global-selector-container .multi-select-options {
  overflow: visible !important;
  position: absolute !important;
  z-index: 99999 !important;
}

/* Days of Service styling */
.days-of-service .days-container {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 1rem !important;
  align-items: center !important;
}

.days-of-service .checkbox {
  display: flex !important;
  align-items: center !important;
  font-size: 0.75rem !important;
  margin: 0 !important;
  padding: 0.25rem 0 !important;
  white-space: nowrap !important;
}

.days-of-service .checkbox input[type="checkbox"] {
  margin-right: 0.5rem !important;
  margin-bottom: 0 !important;
  accent-color: #000000 !important;
  width: 14px !important;
  height: 14px !important;
}

.days-of-service .label {
  margin-bottom: 0.5rem !important;
}

/* Header section styling */
.header-section {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  margin-bottom: 1rem !important;
}

.type-selector {
  flex-shrink: 0 !important;
}

.type-selector .buttons {
  margin: 0 !important;
}

.type-selector .button {
  font-size: 0.7rem !important;
  padding: 0.25rem 0.5rem !important;
  height: auto !important;
  line-height: 1.2 !important;
}
</style>
